import { dockInHeader } from './header-slot.js';

/**
 * The Quickie page (sections/quickie-*.liquid + blocks/_quickie-*.liquid;
 * loaded by sections/quickie-hero):
 * 1. The sticky strip under the nav (always shown; the hero section pads
 *    itself by the strip's height). It assumes The Quickie is being
 *    booked: name + running total, a +chip per add-on, and Checkout. The
 *    chips and the phone sheet's rows come from <template>s each Add-on
 *    block renders (mountAddons) — Liquid can't iterate nested blocks.
 * 2. Upgrades the cart forms (snippets/quickie-buy.liquid) to AJAX:
 *    add-on toggles (+ / ✓ / −, also adding The Quickie), and Book /
 *    Checkout, which adds The Quickie once and goes to /checkout.
 * 3. Keeps every control in sync with the cart. Quantities cap at 1.
 * 4. Before / after sliders: pinned scroll-scrub on desktop, swipe-when-centered on phones (bindCompare).
 * 5. Real-time reaction videos, tap to play with audio (bindReactions).
 * 6. FAQ accordions animate open/close, one open at a time (bindFaq).
 * 7. PDP thumbnail gallery (bindGallery) + add-on info popovers (bindAddonInfo).
 * 8. How-it-works dots fill as the steps scroll past on phones (bindTimeline).
 * Without JS the forms POST to /cart/add natively.
 * Re-binds after theme editor re-renders.
 */
function formatMoney(cents) {
  const amount = cents / 100;
  const text = amount.toLocaleString('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' });
  return text.replace(/\.00$/, '');
}

/**
 * Sticky strip + buttons mirror the cart. The strip assumes The Quickie
 * is being booked: total = Quickie price + add-ons in the cart (the
 * Quickie itself is only added at Checkout, or alongside an add-on).
 */
function renderCart(cart) {
  const bar = document.querySelector('[data-quickie-bar]');
  if (bar) {
    const tier = bar.dataset.tierA;
    const tierPrice = Number(bar.dataset.tierPrice || 0);
    const tierInCart = cart.items.some((item) => String(item.variant_id) === tier);
    const total = cart.total_price + (tierInCart ? 0 : tierPrice);
    for (const totalEl of document.querySelectorAll('[data-quickie-total]')) totalEl.textContent = formatMoney(total);
    // Add-ons chosen (everything in the cart except The Quickie).
    const addonCount = cart.items.filter((item) => String(item.variant_id) !== tier).reduce((n, item) => n + item.quantity, 0);
    for (const badge of document.querySelectorAll('[data-quickie-addons-count]')) {
      badge.textContent = addonCount;
      badge.hidden = addonCount === 0;
    }
  }
  syncButtons(cart);
}

/**
 * Buttons mirror the cart. Text pills show "Added ✓" that flips to
 * "Remove" on hover; icon buttons and chips carry the state in
 * data-in-cart (CSS draws + / ✓ / −). Clicking an in-cart control
 * removes the item (see onAddSubmit).
 */
function syncButtons(cart) {
  const inCart = new Set(cart.items.map((item) => String(item.variant_id)));
  for (const form of document.querySelectorAll('[data-quickie-add]')) {
    const variant = form.querySelector('input[name="id"]').value;
    const button = form.querySelector('button');
    const isIcon = button.classList.contains('quickie-buy__icon') || button.classList.contains('quickie-chip');
    if (!isIcon && !button.dataset.originalHtml) button.dataset.originalHtml = button.innerHTML;
    button.disabled = false;
    if (inCart.has(variant)) {
      button.dataset.inCart = 'true';
      button.setAttribute('aria-pressed', 'true');
      if (!isIcon) {
        button.innerHTML =
          '<span class="quickie-buy__added">Added &check;</span><span class="quickie-buy__remove">Remove</span>';
      }
    } else {
      delete button.dataset.inCart;
      button.setAttribute('aria-pressed', 'false');
      if (!isIcon) button.innerHTML = button.dataset.originalHtml;
    }
  }
}

async function refreshCart() {
  try {
    renderCart(await fetch('/cart.js').then((r) => r.json()));
  } catch (e) {
    /* leave the strip in its idle state */
  }
}

function updateCart(updates) {
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  }).then((r) => {
    if (!r.ok) throw new Error('update failed');
    return r.json();
  });
}

function tierVariant() {
  return document.querySelector('[data-quickie-bar]')?.dataset.tierA || null;
}

/**
 * Add-on toggles: adding an add-on also puts The Quickie in the order
 * (it's assumed); removing one leaves the rest alone. Quantities cap
 * at 1.
 */
function onAddSubmit(event) {
  const form = event.target.closest('[data-quickie-add]');
  if (!form) return;
  event.preventDefault();
  const variant = form.querySelector('input[name="id"]').value;
  const button = form.querySelector('button');
  const removing = button.dataset.inCart === 'true';
  const updates = { [variant]: removing ? 0 : 1 };
  const tier = tierVariant();
  if (!removing && tier && tier !== variant) updates[tier] = 1;
  const original = button.innerHTML;
  button.disabled = true;
  updateCart(updates)
    .then(renderCart)
    .catch(() => {
      // Fall back to the native POST (programmatic submit skips this
      // listener), e.g. when the product isn't published yet.
      button.disabled = false;
      button.innerHTML = original;
      form.submit();
    });
}

/**
 * Book / Checkout: make sure The Quickie is in the order (once), then
 * go straight to checkout. Without JS the form POSTs with return_to.
 */
function onCheckoutSubmit(event) {
  const form = event.target.closest('[data-quickie-checkout]');
  if (!form) return;
  event.preventDefault();
  const variant = form.querySelector('input[name="id"]').value;
  const button = form.querySelector('button');
  button.disabled = true;
  updateCart({ [variant]: 1 })
    .then(() => {
      window.location.href = '/checkout';
    })
    .catch(() => {
      button.disabled = false;
      form.submit();
    });
}

/**
 * Before / after sliders. --pos is the divider: AFTER is revealed from
 * the left edge up to it — 0% = all before, 100% = all after.
 * Every width: each ROW (one case) is pinned below the nav inside
 * a taller container (.quickie-ba-pin); the visitor's own scrolling
 * scrubs the divider through the first half of the hold, then the
 * reveal finishes itself (eased sweep to AFTER) and the page releases.
 * Native scrolling, no hijacking — Lenis-friendly.
 * Dragging the handle takes over for that block until it leaves
 * view. Each block only ever holds ONCE — after it's revealed, it
 * stays on "after" and the pin is collapsed (see releasePin), so
 * scrolling back up flows freely. Reduced motion: static 50/50.
 */
const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const pinsMedia = window.matchMedia('(min-width: 0px)'); // pin on every width (phones too)
const compareFrames = [];
/* Position writes are fractional and EASED: scroll sets a target, and
   a rAF loop glides the visible divider toward it, so wheel/trackpad
   steps and Lenis's frame-to-frame deltas never read as jagged. */
function writeComparePos(frame, pos) {
  frame.style.setProperty('--pos', `${pos.toFixed(2)}%`);
  const range = frame.querySelector('[data-quickie-ba-range]');
  if (range) range.value = Math.round(pos);
  // The corner tag reads BEFORE until the divider passes the midpoint.
  frame.dataset.half = pos >= 50 ? 'after' : 'before';
}
function setComparePos(frame, pos, immediate = false) {
  frame._target = pos;
  if (immediate || frame._current === undefined) {
    frame._current = pos;
    writeComparePos(frame, pos);
    return;
  }
  startCompareLoop();
}
let compareLoop = 0;
function startCompareLoop() {
  if (compareLoop) return;
  const tick = () => {
    let busy = false;
    for (const frame of compareFrames) {
      if (frame._target === undefined) continue;
      const diff = frame._target - frame._current;
      if (Math.abs(diff) < 0.05) {
        if (frame._current !== frame._target) {
          frame._current = frame._target;
          writeComparePos(frame, frame._current);
        }
        continue;
      }
      frame._current += diff * 0.18;
      writeComparePos(frame, frame._current);
      busy = true;
    }
    compareLoop = busy ? requestAnimationFrame(tick) : 0;
  };
  compareLoop = requestAnimationFrame(tick);
}
/* Eased sweep from wherever the divider is now to fully AFTER. */
function swipeCompare(frame) {
  const start = performance.now();
  const from = frame._current || 0;
  const duration = 1400 * ((100 - from) / 100) + 400;
  const tick = (now) => {
    if (frame.dataset.touched || frame.dataset.state !== 'swiping') return;
    const t = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - t, 3);
    setComparePos(frame, from + (100 - from) * eased, true);
    if (t < 1) requestAnimationFrame(tick);
    else frame.dataset.state = 'done';
  };
  requestAnimationFrame(tick);
}
function measurePins() {
  for (const frame of compareFrames) {
    const pin = frame.closest('[data-quickie-ba-pin]');
    const block = pin?.querySelector('.quickie-ba-row');
    if (!pin || !block) continue;
    if (!pinsMedia.matches || pin.dataset.released) {
      if (!pin.dataset.released) pin.style.removeProperty('--pin-height');
      block.style.removeProperty('--pin-top');
      continue;
    }
    const vh = window.innerHeight;
    const travel = vh * 0.7; // matches --ba-travel
    const h = block.offsetHeight;
    // Centered, but never tucked under the sticky nav + strip.
    const barH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--quickie-bar-height')) || 56;
    const minTop = headerHeight() + barH + 16;
    const pinTop = Math.max((vh - h) / 2, minTop);
    pin.style.setProperty('--pin-height', `${h + travel}px`);
    pin.style.setProperty('--pin-top', `${pinTop}px`);
    pin.dataset.travel = travel;
    pin.dataset.blockHeight = h;
    pin.dataset.pinTop = pinTop;
  }
}
/**
 * Once a block has fully revealed and scrolled out above, it's
 * released for good: the pin container collapses to the block's own
 * height (the scroll position is nudged by the same amount so nothing
 * on screen moves), so scrolling back up never holds it again.
 */
function releasePin(frame, pin) {
  if (pin.dataset.released) return;
  pin.dataset.released = 'true';
  const before = pin.offsetHeight;
  pin.style.setProperty('--pin-height', 'auto');
  const block = pin.querySelector('.quickie-ba-row');
  if (block) block.style.position = 'static';
  const travel = before - pin.offsetHeight; // the exact collapse
  if (travel > 0) {
    // Compensate for the collapsed height in the same frame so nothing
    // on screen moves (scroll anchoring is disabled in CSS so the
    // browser doesn't correct it a second time). With Lenis, shift its
    // whole animation state by the same amount — current, target and
    // the in-flight lerp — so any momentum carries on uninterrupted
    // instead of being cut to the current position (that read as a
    // small backwards jump).
    const target = Math.max(window.scrollY - travel, 0);
    const lenis = window.__lenis;
    if (lenis) {
      lenis.animatedScroll = Math.max(lenis.animatedScroll - travel, 0);
      lenis.targetScroll = Math.max(lenis.targetScroll - travel, 0);
      if (lenis.animate) {
        lenis.animate.value = Math.max((lenis.animate.value || 0) - travel, 0);
        lenis.animate.from = Math.max((lenis.animate.from || 0) - travel, 0);
        lenis.animate.to = Math.max((lenis.animate.to || 0) - travel, 0);
      }
    }
    window.scrollTo(0, target);
  }
}
let lastScrollY = window.scrollY;
let scrollIdle = true;
let idleTimer = 0;
function noteScrollActivity() {
  scrollIdle = false;
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    scrollIdle = true;
    updateCompare(); // a pending release can now happen unnoticed
  }, 180);
}
function pageAtRest() {
  const lenis = window.__lenis;
  if (lenis) return lenis.isScrolling === false || Math.abs(lenis.velocity || 0) < 0.05;
  return scrollIdle;
}
function updateCompare() {
  const vh = window.innerHeight;
  const scrollingUp = window.scrollY < lastScrollY;
  lastScrollY = window.scrollY;
  // A block mid-scrub (pinned, in view, not yet done) must not have the
  // layout shift under it — releases of earlier blocks wait for it.
  const scrubbing = compareFrames.some((f) => {
    if (f.dataset.state === 'done' || f.dataset.touched) return false;
    const p = f.closest('[data-quickie-ba-pin]');
    const r = pinsMedia.matches && p ? p.getBoundingClientRect() : f.getBoundingClientRect();
    return r.top < vh && r.bottom > 0;
  });
  for (const frame of compareFrames) {
    if (!frame.isConnected) continue;
    const pin = frame.closest('[data-quickie-ba-pin]');
    // Desktop scrubs against the pinned row; phones judge each card.
    const rect = pinsMedia.matches && pin ? pin.getBoundingClientRect() : frame.getBoundingClientRect();
    const done = frame.dataset.state === 'done';
    if (done) {
      // Fully revealed: stays on "after". On desktop, collapse the pin
      // once it's above the viewport so it never holds on the way back —
      // but only when nothing else is scrubbing, unless this pin is
      // about to scroll back into view.
      if (pin && pinsMedia.matches && !pin.dataset.released && rect.bottom < 0) {
        // Collapse only while the page is at rest (a layout shift plus
        // scroll compensation is invisible then), unless the block is
        // about to come back into view on the way up.
        const reentering = scrollingUp && rect.bottom > -vh * 0.25;
        if ((!scrubbing && pageAtRest()) || reentering) releasePin(frame, pin);
      }
      continue;
    }
    if (rect.bottom < 0 || rect.top > vh) {
      delete frame.dataset.touched;
      frame.dataset.state = 'armed';
      setComparePos(frame, 0, true);
      continue;
    }
    if (frame.dataset.touched) continue;
    if (pinsMedia.matches && pin) {
      // Pinned: the divider tracks the scroll through the first half of
      // the container's extra height, then finishes the reveal on its
      // own (eased sweep to fully AFTER).
      if (frame.dataset.state !== 'armed') continue;
      const travel = Number(pin.dataset.travel || vh * 0.7);
      const pinTop = Number(pin.dataset.pinTop || 0);
      const progress = Math.min(Math.max((pinTop - rect.top) / travel, 0), 1);
      if (progress >= 0.5) {
        frame.dataset.state = 'swiping';
        swipeCompare(frame);
      } else {
        setComparePos(frame, progress * 100);
      }
    } else if (frame.dataset.state === 'armed') {
      const center = rect.top + rect.height / 2;
      if (center <= vh / 2) {
        frame.dataset.state = 'swiping';
        swipeCompare(frame);
      }
    }
  }
}
function bindCompare(scope) {
  for (const frame of scope.querySelectorAll('[data-quickie-ba]')) {
    if (frame.dataset.bound) continue;
    frame.dataset.bound = 'true';
    const range = frame.querySelector('[data-quickie-ba-range]');
    range?.addEventListener('input', () => {
      frame.dataset.touched = 'true';
      setComparePos(frame, Number(range.value), true);
    });
    if (prefersReduced.matches) continue;
    frame.dataset.state = 'armed';
    setComparePos(frame, 0, true);
    compareFrames.push(frame);
  }
  if (compareFrames.length && !window.__quickieCompareBound) {
    window.__quickieCompareBound = true;
    let ticking = false;
    const onScroll = () => {
      noteScrollActivity();
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateCompare();
        ticking = false;
      });
    };
    const onResize = () => {
      measurePins();
      onScroll();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    if (document.fonts?.ready) document.fonts.ready.then(onResize);
    window.addEventListener('load', onResize);
  }
  measurePins();
  updateCompare();
}

/**
 * Real-time reactions: tap a card to play its video WITH audio (one
 * at a time); tap again to pause. Ended videos reset to their poster.
 */
function bindReactions(scope) {
  for (const card of scope.querySelectorAll('[data-quickie-reaction]')) {
    if (card.dataset.bound) continue;
    card.dataset.bound = 'true';
    const video = card.querySelector('video');
    const toggle = card.querySelector('[data-quickie-reaction-toggle]');
    if (!video || !toggle) continue;
    const playLabel = toggle.getAttribute('aria-label');
    const pauseLabel = playLabel.replace(toggle.dataset.labelPlay, toggle.dataset.labelPause);
    const setState = (playing) => {
      card.classList.toggle('is-playing', playing);
      toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      toggle.setAttribute('aria-label', playing ? pauseLabel : playLabel);
    };
    toggle.addEventListener('click', () => {
      if (video.paused) {
        for (const other of document.querySelectorAll('[data-quickie-reaction] video')) {
          if (other !== video && !other.paused) other.pause();
        }
        video.muted = false;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
    video.addEventListener('play', () => setState(true));
    video.addEventListener('pause', () => setState(false));
    video.addEventListener('ended', () => {
      video.currentTime = 0;
      video.load(); // back to the poster
      setState(false);
    });
  }
}

/**
 * FAQ accordions: <details> open/close with a soft height + fade
 * (Web Animations API; native toggle for reduced motion / no support).
 */
const FAQ_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
function closeFaq(item) {
  const content = item.querySelector('.quickie-faq__content');
  if (!item.open || item.dataset.animating) return;
  if (prefersReduced.matches || !content?.animate) {
    item.open = false;
    return;
  }
  item.dataset.animating = 'true';
  const height = content.offsetHeight;
  const closing = content.animate(
    [{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
    { duration: 320, easing: FAQ_EASE }
  );
  closing.onfinish = () => {
    item.open = false;
    delete item.dataset.animating;
  };
}
function openFaq(item) {
  const content = item.querySelector('.quickie-faq__content');
  if (item.open || item.dataset.animating) return;
  if (prefersReduced.matches || !content?.animate) {
    item.open = true;
    return;
  }
  item.dataset.animating = 'true';
  item.open = true;
  const height = content.offsetHeight;
  const opening = content.animate(
    [{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }],
    { duration: 420, easing: FAQ_EASE }
  );
  opening.onfinish = () => {
    delete item.dataset.animating;
  };
}
/* One open at a time: opening an item closes any other open one in
   the same list (both animate together). */
function bindFaq(scope) {
  for (const item of scope.querySelectorAll('.quickie-faq__item')) {
    if (item.dataset.bound) continue;
    item.dataset.bound = 'true';
    const summary = item.querySelector('summary');
    if (!summary) continue;
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (item.open) {
        closeFaq(item);
        return;
      }
      const list = item.closest('.quickie-faq__items') || document;
      for (const other of list.querySelectorAll('.quickie-faq__item[open]')) {
        if (other !== item) closeFaq(other);
      }
      openFaq(item);
    });
  }
}

/**
 * PDP gallery: thumbnails switch the active slide; a video slide plays
 * (muted) while active and pauses when it isn't.
 */
function bindGallery(scope) {
  for (const gallery of scope.querySelectorAll('[data-quickie-gallery]')) {
    if (gallery.dataset.bound) continue;
    gallery.dataset.bound = 'true';
    const slides = [...gallery.querySelectorAll('[data-quickie-slide]')];
    // Thumbs: each frame (a Media block) carries its own thumbnail in a
    // <template>; mount them here in frame order — Liquid can count the
    // Gallery's nested blocks but not read them.
    const strip = gallery.querySelector('[data-quickie-thumbs]');
    const thumbs = [];
    if (strip) {
      strip.replaceChildren();
      slides.forEach((slide, i) => {
        const thumb = document.createElement('button');
        thumb.className = 'quickie-gallery__thumb';
        thumb.type = 'button';
        thumb.setAttribute('role', 'tab');
        thumb.setAttribute('aria-selected', 'false');
        thumb.setAttribute('aria-label', (strip.dataset.thumbLabel || '{n}').replace('{n}', i + 1));
        const template = slide.querySelector('template[data-quickie-thumb]');
        if (template) thumb.append(template.content.cloneNode(true));
        strip.append(thumb);
        thumbs.push(thumb);
      });
    }
    const show = (index) => {
      slides.forEach((slide, i) => {
        const on = i === index;
        slide.classList.toggle('is-active', on);
        const video = slide.querySelector('video');
        if (video) {
          if (on) {
            const attempt = video.play();
            if (attempt) attempt.catch(() => {});
          } else {
            video.pause();
          }
        }
      });
      thumbs.forEach((thumb, i) => {
        const on = i === index;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    // Theme editor: selecting a Media block brings its frame forward.
    gallery.addEventListener('shopify:block:select', (event) => {
      const i = slides.indexOf(event.target);
      if (i >= 0) show(i);
    });
    show(0);
  }
}

/**
 * Add-on (i) popovers: CSS shows them on hover/focus; a click pins one
 * open (is-open) until the next click, an outside click, or Esc.
 */
function bindAddonInfo(scope) {
  for (const wrap of scope.querySelectorAll('[data-quickie-addon-info]')) {
    if (wrap.dataset.bound) continue;
    wrap.dataset.bound = 'true';
    const button = wrap.querySelector('.quickie-addon__info');
    if (!button) continue;
    const setOpen = (open) => {
      wrap.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    button.addEventListener('click', () => {
      const opening = !wrap.classList.contains('is-open');
      for (const other of document.querySelectorAll('[data-quickie-addon-info].is-open')) {
        if (other !== wrap) {
          other.classList.remove('is-open');
          other.querySelector('.quickie-addon__info')?.setAttribute('aria-expanded', 'false');
        }
      }
      setOpen(opening);
    });
    document.addEventListener('click', (event) => {
      if (!wrap.contains(event.target)) setOpen(false);
    });
    wrap.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.focus();
      }
    });
  }
}

/**
 * Reactions carousel: mouse drag scrolls the row (touch scrolls
 * natively); a drag suppresses the click that would otherwise play.
 */
function bindCarousel(scope) {
  for (const row of scope.querySelectorAll('[data-quickie-carousel]')) {
    if (row.dataset.bound || !row.classList.contains('quickie-reactions__grid--carousel')) continue;
    row.dataset.bound = 'true';
    let startX = 0;
    let startLeft = 0;
    let dragging = false;
    let moved = false;
    row.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startLeft = row.scrollLeft;
      row.classList.add('is-dragging');
    });
    row.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      row.scrollLeft = startLeft - dx;
    });
    const end = () => {
      if (!dragging) return;
      dragging = false;
      row.classList.remove('is-dragging');
    };
    row.addEventListener('pointerup', end);
    row.addEventListener('pointerleave', end);
    row.addEventListener('click', (event) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);
  }
}

/**
 * Showcase carousel: arrows / dots / keyboard step through 16:9 slides
 * (crossfade + slight slide). The active slide plays muted; its Listen
 * button toggles audio, and only one slide sounds at a time.
 */
function bindShowcase(scope) {
  for (const carousel of scope.querySelectorAll('[data-quickie-showcase]')) {
    if (carousel.dataset.bound) continue;
    carousel.dataset.bound = 'true';
    const slides = [...carousel.querySelectorAll('[data-quickie-showcase-slide]')];
    const dots = [...carousel.querySelectorAll('[data-quickie-showcase-dot]')];
    const current = carousel.querySelector('[data-quickie-showcase-current]');
    let index = 0;
    const show = (next, dir = 1) => {
      const count = slides.length;
      next = ((next % count) + count) % count;
      slides.forEach((slide, i) => {
        const on = i === next;
        slide.classList.toggle('is-leaving-back', !on && dir < 0 && i === index);
        slide.classList.toggle('is-active', on);
        const video = slide.querySelector('video');
        const sound = slide.querySelector('[data-quickie-showcase-sound]');
        if (!video) return;
        if (on) {
          const attempt = video.play();
          if (attempt) attempt.catch(() => {});
        } else {
          video.pause();
          video.muted = true;
          if (sound) {
            sound.setAttribute('aria-pressed', 'false');
            sound.textContent = sound.dataset.labelListen || sound.textContent;
          }
        }
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === next);
        dot.setAttribute('aria-selected', i === next ? 'true' : 'false');
      });
      if (current) current.textContent = next + 1;
      index = next;
    };
    carousel.querySelector('[data-quickie-showcase-prev]')?.addEventListener('click', () => show(index - 1, -1));
    carousel.querySelector('[data-quickie-showcase-next]')?.addEventListener('click', () => show(index + 1, 1));
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.quickieShowcaseDot), 1)));
    carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1, 1);
      if (event.key === 'ArrowLeft') show(index - 1, -1);
    });
    // Theme editor: selecting a slide block brings that slide forward.
    carousel.addEventListener('shopify:block:select', (event) => {
      const i = slides.indexOf(event.target);
      if (i >= 0) show(i, 1);
    });
    for (const sound of carousel.querySelectorAll('[data-quickie-showcase-sound]')) {
      sound.dataset.labelListen = sound.textContent;
      const video = sound.closest('.quickie-showcase__media')?.querySelector('video');
      if (!video) continue;
      // Button follows the video's muted state, however it changes
      // (this click, another slide's Listen, or video-listen.js muting
      // it once the carousel scrolls fully out of view).
      video.addEventListener('volumechange', () => {
        sound.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
        sound.textContent = video.muted ? sound.dataset.labelListen : 'Mute';
      });
      sound.addEventListener('click', () => {
        const on = video.muted;
        for (const other of document.querySelectorAll('video')) {
          if (other !== video) other.muted = true;
        }
        video.muted = !on;
        if (!on) video.volume = 1;
        sound.setAttribute('aria-pressed', on ? 'true' : 'false');
        sound.textContent = on ? 'Mute' : sound.dataset.labelListen;
        const attempt = video.play();
        if (attempt) attempt.catch(() => {});
      });
    }
    // Only play while on screen.
    const io = new IntersectionObserver(([entry]) => {
      const video = slides[index]?.querySelector('video');
      if (!video) return;
      if (entry.isIntersecting) {
        const attempt = video.play();
        if (attempt) attempt.catch(() => {});
      } else {
        video.pause();
      }
    }, { threshold: 0.2 });
    io.observe(carousel);
    show(0);
  }
}

/**
 * Add-ons bottom sheet (phones): the strip's Add-ons button opens it;
 * ✕, the backdrop, or Esc closes it.
 */
function bindAddonsSheet(scope) {
  const sheet = document.querySelector('[data-quickie-addons-sheet]');
  if (!sheet || typeof sheet.showModal !== 'function') return;
  dockInHeader(document.querySelector('.quickie-book-nav')); // phones: header slot
  for (const btn of scope.querySelectorAll('[data-quickie-addons-open]')) {
    if (btn.dataset.bound) continue;
    btn.dataset.bound = 'true';
    btn.addEventListener('click', () => sheet.showModal());
  }
  if (!sheet.dataset.bound) {
    sheet.dataset.bound = 'true';
    sheet.querySelector('[data-quickie-addons-close]')?.addEventListener('click', () => sheet.close());
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) sheet.close();
    });
  }
}

/**
 * The strip's add-on chips and the phone sheet's rows are rendered by
 * each Add-on block as <template>s (blocks/_quickie-addon.liquid) and
 * mounted here in block order; re-run whenever the hero re-renders.
 */
function mountAddons() {
  const clones = (selector) => [...document.querySelectorAll(selector)].map((t) => t.content.cloneNode(true));
  document.querySelector('[data-quickie-bar-addons]')?.replaceChildren(...clones('template[data-quickie-addon-chip]'));
  document.querySelector('[data-quickie-sheet-list]')?.replaceChildren(...clones('template[data-quickie-addon-row]'));
}

/**
 * How it works timeline, phones/tablets (vertical layout): a step's dot
 * fills once its node has scrolled past the middle of the viewport, so
 * the dots light up 1 → 4 on the way down and clear on the way back up.
 * Desktop's horizontal row keeps its reveal choreography only.
 */
function bindTimeline(scope) {
  const vertical = window.matchMedia('(max-width: 989px)');
  for (const list of scope.querySelectorAll('.quickie-timeline')) {
    if (list.dataset.bound) continue;
    list.dataset.bound = 'true';
    const steps = [...list.querySelectorAll('.quickie-timeline__step')];
    let ticking = false;
    const update = () => {
      ticking = false;
      const line = window.innerHeight * 0.55;
      for (const step of steps) {
        const node = step.querySelector('.quickie-timeline__node') || step;
        const rect = node.getBoundingClientRect();
        step.classList.toggle('is-passed', vertical.matches && rect.top + rect.height / 2 < line);
      }
    };
    const request = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    vertical.addEventListener('change', request);
    update();
  }
}

function headerHeight() {
  const header = document.querySelector('.shopify-section:has(> header)');
  return header ? header.offsetHeight : 0;
}

function bind() {
  const bar = document.querySelector('[data-quickie-bar]');
  if (!bar || bar.dataset.bound) return;
  bar.dataset.bound = 'true';
  bar.hidden = false;
  mountAddons();
  document.addEventListener('submit', (event) => {
    onAddSubmit(event);
    onCheckoutSubmit(event);
  });
  refreshCart();
  // The strip sits directly under the sticky nav and is always shown;
  // the section pads itself by the strip's height (--quickie-bar-height).
  const measure = () => {
    document.documentElement.style.setProperty('--header-height', `${headerHeight()}px`);
    // On phones the strip is hidden (the BOOK pill lives in the nav), so
    // it contributes no height.
    const strip = window.matchMedia('(max-width: 749px)').matches ? 0 : bar.offsetHeight;
    document.documentElement.style.setProperty('--quickie-bar-height', `${strip}px`);
  };
  measure();
  window.addEventListener('resize', measure);
}

document.addEventListener('shopify:section:load', (event) => {
  bind();
  bindCompare(event.target);
  bindReactions(event.target);
  bindFaq(event.target);
  bindGallery(event.target);
  bindAddonInfo(event.target);
  bindCarousel(event.target);
  bindShowcase(event.target);
  bindAddonsSheet(event.target);
  bindTimeline(event.target);
});
bind();
bindCompare(document);
bindReactions(document);
bindFaq(document);
bindGallery(document);
bindAddonInfo(document);
bindCarousel(document);
bindShowcase(document);
bindAddonsSheet(document);
bindTimeline(document);
